import { useEffect, useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import {
  AddonAdditionalTax,
  getAddonAdditionalTaxes,
  createAddonAdditionalTax,
  deleteAddonAdditionalTax,
} from '../lib/tax-rates';
import { updateServiceAddon } from '../lib/services';

interface AddonTaxSettingsProps {
  addonId: string;
  initialTaxExempt?: boolean;
}

/**
 * Per-add-on tax configuration: an exempt toggle + optional additional rates
 * (e.g. PST), mirroring how services are configured. Edits the catalog directly.
 */
export default function AddonTaxSettings({ addonId, initialTaxExempt = false }: AddonTaxSettingsProps) {
  const [taxExempt, setTaxExempt] = useState(initialTaxExempt);
  const [savingExempt, setSavingExempt] = useState(false);
  const [taxes, setTaxes] = useState<AddonAdditionalTax[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newRate, setNewRate] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    getAddonAdditionalTaxes(addonId)
      .then((rows) => { if (active) setTaxes(rows); })
      .catch(() => { /* non-fatal */ })
      .finally(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, [addonId]);

  const handleToggleExempt = async () => {
    const next = !taxExempt;
    setTaxExempt(next);
    setSavingExempt(true);
    try {
      await updateServiceAddon(addonId, { tax_exempt: next });
    } catch {
      setTaxExempt(!next); // revert on failure
    } finally {
      setSavingExempt(false);
    }
  };

  const handleAdd = async () => {
    const rate = parseFloat(newRate);
    if (!newName.trim() || !rate || rate <= 0 || rate > 100) return;
    setBusy(true);
    try {
      const tax = await createAddonAdditionalTax(addonId, newName.trim(), rate);
      setTaxes((prev) => [...prev, tax]);
      setNewName('');
      setNewRate('');
      setIsAdding(false);
    } catch {
      /* non-fatal */
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id: string) => {
    setTaxes((prev) => prev.filter((t) => t.id !== id));
    try {
      await deleteAddonAdditionalTax(id);
    } catch {
      /* reload on failure */
      getAddonAdditionalTaxes(addonId).then(setTaxes).catch(() => {});
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-gray-200/70">
      <label className="flex items-center justify-between cursor-pointer select-none">
        <span className="text-[13px] font-medium text-gray-700">Tax exempt</span>
        <input
          type="checkbox"
          checked={taxExempt}
          disabled={savingExempt}
          onChange={handleToggleExempt}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500/30"
        />
      </label>

      {!taxExempt && (
        <div className="mt-2">
          <div className="flex items-center justify-between">
            <span className="text-[12px] font-medium text-gray-500">Additional taxes</span>
            {!isAdding && (
              <button
                type="button"
                onClick={() => setIsAdding(true)}
                className="inline-flex items-center gap-1 text-[12px] font-medium text-blue-600 hover:text-blue-700"
              >
                <Plus className="h-3.5 w-3.5" /> Add
              </button>
            )}
          </div>

          {loaded && taxes.length === 0 && !isAdding && (
            <p className="text-[12px] text-gray-400 mt-1">Only the business tax rates apply.</p>
          )}

          {taxes.length > 0 && (
            <div className="mt-1.5 space-y-1">
              {taxes.map((t) => (
                <div key={t.id} className="flex items-center justify-between text-[13px]">
                  <span className="text-gray-700">{t.name} ({t.rate}%)</span>
                  <button
                    type="button"
                    onClick={() => handleDelete(t.id)}
                    className="text-gray-400 hover:text-red-600"
                    aria-label="Remove tax"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {isAdding && (
            <div className="mt-2 flex items-center gap-2">
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. PST"
                className="flex-1 min-w-0 px-2.5 py-1.5 bg-white border border-gray-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
              <div className="relative w-20">
                <input
                  value={newRate}
                  onChange={(e) => setNewRate(e.target.value)}
                  placeholder="0"
                  inputMode="decimal"
                  className="w-full pr-6 pl-2.5 py-1.5 bg-white border border-gray-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[12px] text-gray-400">%</span>
              </div>
              <button
                type="button"
                onClick={handleAdd}
                disabled={busy || !newName.trim() || !newRate}
                className="px-2.5 py-1.5 text-[12px] font-semibold text-white bg-blue-600 rounded-lg disabled:opacity-50"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => { setIsAdding(false); setNewName(''); setNewRate(''); }}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Cancel"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
